/** @jsx jsx */
import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import Img from "gatsby-image";
import { Box, Flex, Button, Heading, Text, jsx } from "theme-ui";
import { IoIosAdd, IoIosRemove } from "react-icons/io";
import { CartContext } from "../../../provider/cart-provider";
import useOnClickOutside from "../../../hooks/useOutsideClick";
import styles from "./product-card.style";

import { debounce } from "lodash";

type PropsType = {
  price: any;
  title: string;
  thumbnail: any;
  variants: any;
  shopifyId: string;
  prevPrice?: string;
  availableForSale: boolean;
  listView?: boolean;
};

const ProductCard: React.FC<PropsType> = (product) => {
  const {
    title,
    price,
    variants,
    variants: [initialVariant],
    thumbnail,
    prevPrice,
    listView = false,
  } = product;

  const {
    addVariantToCart,
    updateLineItem,
    removeLineItem,
    store: { client, updating, checkout },
  } = useContext(CartContext);

  const counterRef = useRef(null!);
  const [showCounter, setShowCounter] = useState(false);
  useOnClickOutside(counterRef, () => setShowCounter(false));

  const [variant, setVariant] = useState({ ...initialVariant });
  const productVariant = variant;
  const lineItemIndex = checkout.lineItems.findIndex(
    (item) => item.variant.id === productVariant.shopifyId
  );
  const lineItem =
    lineItemIndex > -1 ? checkout.lineItems[lineItemIndex] : false;
  const lineItemQuantity = lineItem ? lineItem.quantity : 0;
  const [quantity, setQuantity] = useState(0);
  const [available, setAvailable] = useState(productVariant.availableForSale);
  const checkAvailability = useCallback(
    (productId) => {
      client.product.fetch(productId).then((fetchedProduct) => {
        // this checks the currently selected variant for availability
        const result = fetchedProduct.variants.filter(
          (variant) => variant.id === productVariant.shopifyId
        );
        if (result && result.length > 0) {
          setAvailable(result[0].available);
        }
      });
    },
    [client.product, productVariant.shopifyId, variants]
  );

  useEffect(() => {
    setQuantity(lineItemQuantity);
  }, [lineItemQuantity]);

  useEffect(() => {
    checkAvailability(product.shopifyId);
  }, [productVariant, checkAvailability, product.shopifyId]);

  // debounce cart update
  const delayedCartUpdate = useCallback(
    debounce(
      // @ts-ignore
      (client, checkout, lineItem, quantity) =>
        updateCart(client, checkout, lineItem, quantity),
      1000
    ),
    []
  );

  const updateCart = (client, checkout, lineItem, quantity = 0) => {
    if (quantity > 0) {
      updateLineItem(client, checkout.id, lineItem.id, quantity);
    } else {
      removeLineItem(client, checkout.id, lineItem.id);
      setShowCounter(false);
    }
  };

  const handleQuantityChange = (quantity) => {
    if (quantity > 0) {
      setQuantity(quantity);
      delayedCartUpdate(client, checkout, lineItem, quantity);
    } else {
      setQuantity(0);
      setShowCounter(false);
      delayedCartUpdate(client, checkout, lineItem, quantity);
    }
  };

  const handleAddToCart = () => {
    setShowCounter(true);
    if (quantity < 1) {
      setQuantity(1);
      addVariantToCart(productVariant.shopifyId, 1);
    }
  };

  return (
    <Box
      className={showCounter ? "active" : ""}
      sx={styles.wrapper}
      onClick={() => handleAddToCart()}
    >
      <Box className="image" sx={styles.imageWrapper}>
        {thumbnail ? (
          <Img fluid={thumbnail} alt={title} />
        ) : (
          <image src={require("../../../images/logo.svg")} />
        )}
        <Box className={showCounter ? "isActive" : ""} sx={styles.cart}>
          <Button
            //TODO: show tooltip and change icon if disabled
            sx={styles.cartBtn}
            disabled={!available || updating}
            variant="badge"
            className={quantity > 0 ? "active" : ""}
          >
            {quantity > 0 ? (
              quantity
            ) : (
              <svg width="13" height="12" viewBox="0 0 13 12">
                <path
                  d="M67.213,68.157H65.137l-2.953-3.949a.517.517,0,0,0-.829,0L58.4,68.157H56.326a1.056,1.056,0,0,0-1.056,1.054.987.987,0,0,0,.042.292l1.5,5.3A1.643,1.643,0,0,0,58.392,76h6.756a1.659,1.659,0,0,0,1.587-1.2l1.5-5.305a.509.509,0,0,0,.015-.077l.017-.16a.434.434,0,0,0,0-.05A1.056,1.056,0,0,0,67.213,68.157ZM61.77,65.386l2.072,2.771H59.7Zm0,7.957A1.224,1.224,0,1,1,63,72.118a1.223,1.223,0,0,1-1.227,1.224Z"
                  transform="translate(-55.269 -64)"
                  fill="#5a5a5a"
                />
              </svg>
            )}
          </Button>

          {quantity ? (
            <Box
              className={!available || updating ? "disabled" : ""}
              ref={counterRef}
              sx={styles.cartCounter}
            >
              <Button
                disabled={!available || updating}
                onClick={() => handleQuantityChange(quantity - 1)}
              >
                <IoIosRemove />
              </Button>
              <Box>{quantity}</Box>
              <Button
                disabled={!available || updating}
                onClick={() => handleQuantityChange(quantity + 1)}
              >
                <IoIosAdd />
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box className="content">
        <Flex>
          <Text as="span" sx={styles.price}>
            {price}
          </Text>
        </Flex>
        <Heading as="h4" sx={styles.title}>
          {title}
        </Heading>
      </Box>
    </Box>
  );
};

export default ProductCard;
